import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  useLocation,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Button, EmptyState, Skeleton } from '@/components';
import {
  WorkspaceTablePage,
  type WorkspaceTableListOption,
} from '@/components/workspace/WorkspaceTablePage';
import {
  boardMembersQueryOptions,
  ownBoardRole,
  workspaceBoardsQueryOptions,
} from '@/lib/boards';
import {
  moveCard,
  renameCard,
  useAddCardMember,
  useRemoveCardMember,
  useUpdateCardDueDate,
} from '@/lib/cards';
import {
  boardLabelsQueryOptions,
  useApplyCardLabel,
  useRemoveCardLabel,
} from '@/lib/labels';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { boardListsQueryOptions } from '@/lib/lists';
import { queryKeys } from '@/lib/query-keys';
import { signInHref, useSession } from '@/lib/session';
import { announce } from '@/lib/announce';
import {
  cappedWorkspaceTableBoardSelection,
  selectedWorkspaceTableBoardIdsFromSearch,
  useWorkspaceTable,
  workspaceTableFilterFromSearch,
  workspaceTableFilterSearchParams,
} from '@/lib/workspace-table';
import { workspaceMembersQueryOptions, workspaceQueryOptions } from '@/lib/workspaces';
import { viewerCardPath } from '@/lib/board-face';

interface WorkspaceTableSearch {
  readonly boardIds?: readonly string[] | undefined;
  readonly members?: readonly string[] | undefined;
  readonly labels?: readonly string[] | undefined;
  readonly lists?: readonly string[] | undefined;
  readonly due?: readonly string[] | undefined;
  readonly keyword?: string | undefined;
}

export const Route = createFileRoute('/w/$workspaceId/table')({
  staticData: { layout: 'full-bleed' },
  validateSearch: (search: Record<string, unknown>): WorkspaceTableSearch => {
    const boardIds = selectedWorkspaceTableBoardIdsFromSearch(search.boardIds);
    return {
      ...(boardIds.length === 0 ? {} : { boardIds }),
      ...workspaceTableSearchFromRecord(search),
    };
  },
  component: WorkspaceTableRoute,
});

function WorkspaceTableRoute() {
  const { t } = useInterfaceLanguage();
  const { workspaceId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const router = useRouter();
  const rawSearch = router.history.location.search;
  const search = Route.useSearch();
  const queryClient = useQueryClient();
  const {
    profile,
    isPending: sessionPending,
    isSignedOut,
    error: sessionError,
  } = useSession();
  const updateDueDate = useUpdateCardDueDate();
  const renameWorkspaceCard = useMutation({
    mutationFn: (variables: { readonly cardId: string; readonly title: string }) =>
      renameCard(variables.cardId, { title: variables.title.trim() }),
    onError: () => announce(t('workspaceViews.table.renameFailed')),
  });
  const moveWorkspaceCard = useMutation({
    mutationFn: (variables: { readonly cardId: string; readonly toListId: string }) =>
      moveCard(variables.cardId, {
        listId: variables.toListId,
        previousCardId: null,
        nextCardId: null,
      }),
    onError: () => announce(t('workspaceViews.table.moveFailed')),
  });
  const applyCardLabel = useApplyCardLabel();
  const removeCardLabel = useRemoveCardLabel();
  const addCardMember = useAddCardMember();
  const removeCardMember = useRemoveCardMember();

  const canFetch = !isSignedOut && !sessionPending && profile !== null;
  const workspace = useQuery({
    ...workspaceQueryOptions(workspaceId),
    enabled: canFetch,
  });
  const boards = useQuery({
    ...workspaceBoardsQueryOptions(workspaceId, 'open'),
    enabled: canFetch,
  });
  const members = useQuery({
    ...workspaceMembersQueryOptions(workspaceId),
    enabled: canFetch,
  });
  const rawParams = new URLSearchParams(rawSearch);
  const rawBoardIds = selectedWorkspaceTableBoardIdsFromSearch(
    rawParams.getAll('boardIds'),
  );
  const explicitBoardIds = rawParams.has('boardIds')
    ? rawBoardIds.length === 0
      ? (search.boardIds ?? [])
      : rawBoardIds
    : null;
  const selectedBoardIds =
    explicitBoardIds === null
      ? cappedWorkspaceTableBoardSelection((boards.data ?? []).map((board) => board.id))
      : cappedWorkspaceTableBoardSelection(explicitBoardIds);
  const filter = workspaceTableFilterFromSearch(rawSearch);
  const table = useWorkspaceTable(
    { workspaceId, boardIds: selectedBoardIds, filter },
    canFetch && selectedBoardIds.length > 0,
  );
  const selectedBoards = (boards.data ?? []).filter((board) =>
    selectedBoardIds.includes(board.id),
  );
  const labelQueries = useQueries({
    queries: selectedBoards.map((board) => ({
      ...boardLabelsQueryOptions(board.id),
      enabled: canFetch,
    })),
  });
  const listQueries = useQueries({
    queries: selectedBoards.map((board) => ({
      ...boardListsQueryOptions(board.id, 'open'),
      enabled: canFetch,
    })),
  });
  const memberQueries = useQueries({
    queries: selectedBoards.map((board) => ({
      ...boardMembersQueryOptions(board.id),
      enabled: canFetch,
    })),
  });
  const labels = dedupeById(labelQueries.flatMap((query) => query.data ?? []));
  const lists = selectedBoards.flatMap<WorkspaceTableListOption>((board, index) =>
    (listQueries[index]?.data ?? []).map((list) => ({
      id: list.id,
      title: list.title,
      boardId: board.id,
      boardTitle: board.title,
    })),
  );
  const boardMembers = selectedBoards.flatMap((board, index) =>
    (memberQueries[index]?.data ?? []).map((member) => ({
      ...member,
      boardId: board.id,
    })),
  );
  const editableBoardIds = selectedBoards
    .filter((board, index) => {
      const role = ownBoardRole(memberQueries[index]?.data, profile?.id);
      return !board.closed && (role === 'admin' || role === 'normal');
    })
    .map((board) => board.id);
  const loadingFilters =
    members.isPending ||
    labelQueries.some((query) => query.isPending) ||
    listQueries.some((query) => query.isPending) ||
    memberQueries.some((query) => query.isPending);

  function refreshTableCard(boardId: string, cardId: string): void {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.workspaces.table(workspaceId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.boards.table(boardId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.boards.contents(boardId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.cards.detail(cardId),
    });
  }

  function replaceTableSearch(next: {
    readonly boardIds: readonly string[];
    readonly filter: ReturnType<typeof workspaceTableFilterFromSearch>;
  }): void {
    const params = new URLSearchParams();
    for (const boardId of next.boardIds) params.append('boardIds', boardId);
    for (const [key, value] of workspaceTableFilterSearchParams(next.filter)) {
      params.append(key, value);
    }
    const query = params.toString();
    router.history.replace(`${location.pathname}${query === '' ? '' : `?${query}`}`);
    router.history.flush();
  }

  if (isSignedOut) {
    return (
      <WorkspaceTableFrame title={t('workspaceViews.table.title')}>
        <EmptyState
          tone="neutral"
          size="panel"
          headingLevel={2}
          title={t('workspaceViews.table.signInTitle')}
          body={t('workspaceViews.table.signInBody')}
          action={
            <Button
              as="a"
              variant="primary"
              href={signInHref({ pathname: `/w/${workspaceId}/table`, search: '' })}
            >
              {t('workspaceViews.table.signInAction')}
            </Button>
          }
        />
      </WorkspaceTableFrame>
    );
  }

  if (sessionPending) {
    return (
      <WorkspaceTableFrame title={t('workspaceViews.table.title')}>
        <WorkspaceTablePending />
      </WorkspaceTableFrame>
    );
  }

  if (profile === null) {
    return (
      <WorkspaceTableFrame title={t('workspaceViews.table.title')}>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={t('workspaceViews.table.accountErrorTitle')}
          body={sessionError?.message ?? t('workspaceViews.table.genericError')}
        />
      </WorkspaceTableFrame>
    );
  }

  const loadError = workspace.error ?? boards.error;
  if (loadError !== null) {
    const notFound = loadError.code === 'not_found';
    return (
      <WorkspaceTableFrame title={t('workspaceViews.table.title')}>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={
            /* ⚠ EM DASH, matching the calendar route. The views and saved-view
               routes write the same sentence with a HYPHEN; both are bound. */
            notFound
              ? t('workspaceViews.table.notFound')
              : t('workspaceViews.table.loadFailedTitle')
          }
          {...(notFound ? {} : { body: loadError.message })}
        />
      </WorkspaceTableFrame>
    );
  }

  const workspaceName =
    workspace.data?.name !== undefined && workspace.data.name.trim() !== ''
      ? workspace.data.name
      : t('workspaceViews.table.workspaceFallback');

  return (
    <WorkspaceTablePage
      workspaceName={workspaceName}
      boards={boards.data ?? []}
      selectedBoardIds={selectedBoardIds}
      editableBoardIds={editableBoardIds}
      filter={filter}
      members={members.data ?? []}
      boardMembers={boardMembers}
      labels={labels}
      lists={lists}
      table={table.data}
      loadingBoards={boards.isPending}
      loadingFilters={loadingFilters}
      loadingTable={table.isPending && selectedBoardIds.length > 0}
      error={table.error?.message}
      onBoardSelectionChange={(boardIds) =>
        replaceTableSearch({
          boardIds: cappedWorkspaceTableBoardSelection(boardIds),
          filter,
        })
      }
      onFilterChange={(nextFilter) =>
        replaceTableSearch({ boardIds: selectedBoardIds, filter: nextFilter })
      }
      onOpenCard={(card) =>
        void navigate({
          href: viewerCardPath(card),
        })
      }
      onRenameCard={(cardId, boardId, _listId, title) => {
        renameWorkspaceCard.mutate(
          { cardId, title },
          { onSuccess: () => refreshTableCard(boardId, cardId) },
        );
      }}
      onMoveCard={(intent) => {
        moveWorkspaceCard.mutate(
          { cardId: intent.cardId, toListId: intent.toListId },
          { onSuccess: () => refreshTableCard(intent.boardId, intent.cardId) },
        );
      }}
      onSetDueDate={(cardId, boardId, dueAt) => {
        updateDueDate.mutate(
          { cardId, boardId, dueAt },
          { onSuccess: () => refreshTableCard(boardId, cardId) },
        );
      }}
      onAddLabel={(cardId, boardId, labelId) => {
        applyCardLabel.mutate(
          { cardId, boardId, labelId },
          { onSuccess: () => refreshTableCard(boardId, cardId) },
        );
      }}
      onRemoveLabel={(cardId, boardId, labelId) => {
        removeCardLabel.mutate(
          { cardId, boardId, labelId },
          { onSuccess: () => refreshTableCard(boardId, cardId) },
        );
      }}
      onAddMember={(cardId, boardId, userId) => {
        addCardMember.mutate(
          { cardId, boardId, userId },
          { onSuccess: () => refreshTableCard(boardId, cardId) },
        );
      }}
      onRemoveMember={(cardId, boardId, userId) => {
        removeCardMember.mutate(
          { cardId, boardId, userId },
          { onSuccess: () => refreshTableCard(boardId, cardId) },
        );
      }}
      onRetry={() => {
        void workspace.refetch();
        void boards.refetch();
        void members.refetch();
        for (const query of labelQueries) void query.refetch();
        for (const query of listQueries) void query.refetch();
        for (const query of memberQueries) void query.refetch();
        void table.refetch();
      }}
    />
  );
}

function WorkspaceTableFrame({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8 sm:px-6">
      <h1 className="font-display text-2xl font-semibold tracking-tight text-ink">
        {title}
      </h1>
      {children}
    </div>
  );
}

function WorkspaceTablePending() {
  return (
    <div aria-busy="true" className="flex flex-col gap-4">
      <Skeleton shape="block" count={3} />
    </div>
  );
}

function workspaceTableSearchFromRecord(
  search: Record<string, unknown>,
): Pick<WorkspaceTableSearch, 'members' | 'labels' | 'lists' | 'due' | 'keyword'> {
  const filter = workspaceTableFilterFromSearch(search);
  return {
    ...(filter.members.length === 0 ? {} : { members: filter.members }),
    ...(filter.labels.length === 0 ? {} : { labels: filter.labels }),
    ...(filter.lists.length === 0 ? {} : { lists: filter.lists }),
    ...(filter.due.length === 0 ? {} : { due: filter.due }),
    ...(filter.keyword === '' ? {} : { keyword: filter.keyword }),
  };
}

function dedupeById<T extends { readonly id: string }>(items: readonly T[]): T[] {
  const seen = new Set<string>();
  const deduped: T[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    deduped.push(item);
  }
  return deduped;
}
